import React, { useCallback, useMemo, useState } from 'react';
import _ from 'lodash';
import Box from '@amzn/meridian/box';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Input from '@amzn/meridian/input';
import Tag from '@amzn/meridian/tag';
import { FormHandler } from 'src/hooks/useForm';
import { createSearchRegExp } from 'src/utils/searchUtils';
import { useAppSelector } from 'src/store/store';
import { TaskGroupTableDataRow } from 'src/store/adminConfigPageSlice/taskConfigPageSlice/taskConfigPageSlice';

// **** Types **** //

interface TaskGroupModalFormProps {
  form: FormHandler<TaskGroupTableDataRow>;
}

// **** Components **** //

/**
 * Default component
 */
const TaskGroupModalForm = (props: TaskGroupModalFormProps) => {
  const { form } = props;
  const { regions, taskTableData: taskOptions } = useAppSelector((state) => state.taskConfigPage);
  const [regionSearchQuery, setRegionSearchQuery] = useState('');
  const [taskSearchQuery, setTaskSearchQuery] = useState('');

  // When the region changes, reset the selected tasks and task options.
  const regionFilteredTaskOptions = useMemo(() => {
    if (form.formData.region) {
      return taskOptions.filter((t) => t.regions.includes(form.formData.region));
    } else {
      return taskOptions;
    }
  }, [form.formData.region, taskOptions]);

  // Search regions
  const matchedRegionOptions = useMemo(() => {
    const searchRegExp = createSearchRegExp(regionSearchQuery);
    return regions
      .map((region) => ({ label: region.name, value: region.name }))
      .filter((option) => !regionSearchQuery || searchRegExp.test(option.label));
  }, [regions, regionSearchQuery]);

  // Search Tasks
  const matchedTaskOptions = useMemo(() => {
    const searchRegExp = createSearchRegExp(taskSearchQuery);
    return regionFilteredTaskOptions
      .filter((t) => !taskSearchQuery || searchRegExp.test(t.label))
      .map((t) => ({ label: t.label, value: t.id }));
  }, [taskSearchQuery, regionFilteredTaskOptions]);

  // Set the selected-tasks from an array of ids
  const setTasks = useCallback(
    (taskIds: number[]) => {
      const tasks = regionFilteredTaskOptions.filter((t) => taskIds.includes(t.id));
      form.handleChange({ tasks });
    },
    [regionFilteredTaskOptions],
  );

  // Remove a selected-task
  const dismissTask = useCallback(
    (taskId: number) => {
      const tasks = form.formData.tasks.filter((t) => t.id != taskId);
      form.handleChange({ tasks });
    },
    [form.formData.tasks],
  );

  return (
    <Box className="view-and-edit-tasks-modal-form" spacingInset="100 100 300 100">
      {/* Name */}
      <Box spacingInset="none none 200 none">
        <Text>Group Name*</Text>
      </Box>
      <Input
        type="text"
        placeholder="Enter group name..."
        value={form.formData.label}
        onChange={(label: string) => form.handleChange({ label })}
        error={form.errors.label?.error}
        errorMessage={form.errors.label?.errorMessage}
      />
      <Box spacingInset="300" />

      {/* Region */}
      <Text>Region/State/Province*</Text>
      <Select
        searchQuery={regionSearchQuery}
        onSearch={(v) => setRegionSearchQuery(v)}
        placeholder="Select region..."
        value={form.formData.region}
        // Reset "tasks" when the region updates
        onChange={(region: string) => form.updatePartial({ region, tasks: [] })}
        error={form.errors.region?.error}
        errorMessage={form.errors.region?.errorMessage}
      >
        {matchedRegionOptions.map((o) => (
          <SelectOption label={o.label} value={o.value} key={o.value} />
        ))}
      </Select>
      <Box spacingInset="300" />

      {/* Child Tasks */}
      <Text>Add Tasks to Group</Text>
      <Select
        searchQuery={taskSearchQuery}
        onSearch={(v) => setTaskSearchQuery(v)}
        placeholder={
          form.formData.region
            ? !!matchedTaskOptions.length
              ? 'Select...'
              : 'No Tasks avaialble for this region'
            : 'Must select region first'
        }
        value={form.formData.tasks.map((t) => t.id)}
        onChange={setTasks}
        error={form.errors.tasks?.error}
        errorMessage={form.errors.tasks?.errorMessage}
        disabled={!form.formData.region || !matchedTaskOptions.length}
      >
        {matchedTaskOptions.map((o) => (
          <SelectOption label={o.label} value={o.value} key={o.value} />
        ))}
      </Select>
      <Box spacingInset="300" />

      {/* Display selected child tasks */}
      <Box className="tags-container">
        {form.formData.tasks.map((task) => (
          <Tag key={task.id} type="neutral" onClose={() => dismissTask(task.id)}>
            {task.label}
          </Tag>
        ))}
      </Box>
    </Box>
  );
};

// **** Export **** //

export default TaskGroupModalForm;
